import { useMemo } from 'react';
import { SectionCard, fmtTRY } from '../yeni-design/primitives';
import { miktarMetni, oranMetni } from '../satis-analiz/bicim';
import { gunMetni } from './bicimYardimcilari';
import type { StokKaynaklari } from './ekranKaynaklari';
import { kategoriSuz } from './kategori';
import { enYavasUrunler, fazlaAlimlar } from './toplama';
import { useStokSorgu, type StokSorguFiltresi } from './useStokVerisi';

interface DevirHam {
  stok_kod: string; stok_ad: string; kategori: string;
  ort_envanter_deger: number; donem_cogs: number; etkin_gun: number;
}
interface SellHam { stok_kod: string; stok_ad: string; kategori: string; alinan: number; satilan: number }

/** En yavas donenler (990048) ve alinip satilamayanlar (990046): ozet kartlarla ayni anahtar, ek istek yok. */
export function UrunAkisBlok({ kaynaklar, f, kategori }: {
  kaynaklar: StokKaynaklari; f: StokSorguFiltresi; kategori: string | null;
}) {
  const devirQ = useStokSorgu<DevirHam>(kaynaklar.devirHizi, f);
  const sellQ = useStokSorgu<SellHam>(kaynaklar.sellThrough, f);
  const yavas = useMemo(() => enYavasUrunler(kategoriSuz(devirQ.data?.data ?? [], kategori)), [devirQ.data, kategori]);
  const fazla = useMemo(() => fazlaAlimlar(kategoriSuz(sellQ.data?.data ?? [], kategori)), [sellQ.data, kategori]);

  if (yavas.length === 0 && fazla.length === 0) return null;
  return (
    <div data-testid="akis-urunler" className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
      {yavas.length > 0 && (
        <div data-testid="akis-yavas" className="min-w-0 flex flex-col">
          <SectionCard className="flex-1" title="En yavaş dönen ürünler" subtitle={`Dönemde satışı olanlardan ilk ${yavas.length}`}>
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="text-ink-400 text-left">
                  <th className="font-normal py-1">Ürün</th>
                  <th className="font-normal py-1 text-right whitespace-nowrap">Ort. stok</th>
                  <th className="font-normal py-1 pl-2 text-right">Dönüş</th>
                </tr>
              </thead>
              <tbody>
                {yavas.map((u) => (
                  <tr key={u.stokKod} className="border-t border-border">
                    <td className="py-1.5 pr-2 text-ink-800 truncate max-w-0 w-full">{u.stokAd}</td>
                    <td className="py-1.5 text-right tnum text-ink-500 whitespace-nowrap">{fmtTRY(u.ortStok)}</td>
                    <td className="py-1.5 pl-2 text-right tnum text-ink-900 whitespace-nowrap">{gunMetni(u.gun)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SectionCard>
        </div>
      )}
      {fazla.length > 0 && (
        <div data-testid="akis-fazla-alim" className="min-w-0 flex flex-col">
          <SectionCard className="flex-1" title="Alınıp satılamayanlar" subtitle="Alınan ile satılan farkı en büyük ürünler">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="text-ink-400 text-left">
                  <th className="font-normal py-1">Ürün</th>
                  <th className="font-normal py-1 text-right">Alınan</th>
                  <th className="font-normal py-1 pl-2 text-right">Satılan</th>
                  <th className="font-normal py-1 pl-2 text-right">Oran</th>
                </tr>
              </thead>
              <tbody>
                {fazla.map((u) => (
                  <tr key={u.stokKod} className="border-t border-border">
                    <td className="py-1.5 pr-2 text-ink-800 truncate max-w-0 w-full">{u.stokAd}</td>
                    <td className="py-1.5 text-right tnum text-ink-500 whitespace-nowrap">{miktarMetni(u.alinan)}</td>
                    <td className="py-1.5 pl-2 text-right tnum text-ink-500 whitespace-nowrap">{miktarMetni(u.satilan)}</td>
                    <td className="py-1.5 pl-2 text-right tnum text-ink-900 whitespace-nowrap">{oranMetni(u.oran, 1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SectionCard>
        </div>
      )}
    </div>
  );
}
